import React, { ReactNode } from 'react';
import {
  ThemeProvider,
  StyledEngineProvider,
  createTheme,
  responsiveFontSizes,
  PaletteColor,
  PaletteColorOptions,
} from '@mui/material/styles';
import { svSE } from '@mui/material/locale';
import '@mui/material/styles/createPalette';

export const oldColors = {
  primary: '#0e204d',
  secondary: '#1e2e29',
  tertiary: '#060d28',
  accent: '#e8e9ea',
  accentLight: '#f9f9f9',
  accentText: '#646678',
  lightText: '#b4bbc8',
  darkText: '#808080',
  background: '#F3F3F5',
  darkBlue: '#141d40',
};

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    contrast: true;
    accent: true;
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides {
    injured: true;
  }
}

interface PaletteShades {
  p10: string;
  p15: string;
  p30: string;
}

interface PaletteMenu {
  main: string;
  light: string;
  background: string;
  menu: string;
  item: string;
  text: string;
  textGroup: string;
}

declare module '@mui/material/styles/createPalette' {
  interface Palette {
    menu: PaletteMenu;
    shades: PaletteShades;

    // Temp
    contrast: PaletteColor;
    accent: PaletteColor;

    injured: PaletteColor;
  }
  interface PaletteOptions {
    menu: PaletteMenu;
    shades: PaletteShades;

    // Temp
    contrast: PaletteColorOptions;
    accent: PaletteColorOptions;

    injured: PaletteColorOptions;
  }
}

declare module '@mui/material/styles' {
  interface TypographyVariants {
    italic: React.CSSProperties;
    bold: React.CSSProperties;
    'body1-bold': React.CSSProperties;
    'body2-bold': React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    italic?: React.CSSProperties;
    bold?: React.CSSProperties;
    'body1-bold': React.CSSProperties;
    'body2-bold': React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    italic: true;
    bold: true;
    'body1-bold': true;
    'body2-bold': true;
  }
}

const spacing = (i = 1) => i * 5;

// https://mui.com/material-ui/customization/palette/#main-content

let theme = createTheme(
  {
    spacing: spacing(),
    palette: {
      primary: {
        main: oldColors.primary,
        // main: '#1976D2',  // New palette, skipped for now
        // dark: '#1565C0',
        // light: '#42A5F5',
        // contrastText: '#FFFFFF',
      },
      shades: {
        p10: '#0e204d1a', // oldColors.primary 10%
        p15: '#0e204d26', // oldColors.primary 15%
        p30: '#0e204d4d', // oldColors.primary 30%
      },
      menu: {
        main: '#003168',
        light: '#003168',
        background: '#002248',
        menu: '#003168',
        item: '#214d82',
        text: '#FFFFFF',
        textGroup: '#ffffff7f',
      },
      contrast: {
        main: '#ffffff',
      },
      accent: {
        main: '#ffffff',
      },
      background: {
        default: '#fafafa',
      },
      injured: {
        main: '#f57c00',
        contrastText: '#ffffff', // Must be inluded (https://stackoverflow.com/a/69741354)
      },
    },
    typography: {
      italic: {
        fontStyle: 'italic',
      },
      bold: {
        fontWeight: 'bold',
      },
      'body1-bold': {
        // Extending it below
      },
      'body2-bold': {
        // Extending it below
      },
    },
    components: {
      MuiTypography: {
        defaultProps: {
          variantMapping: {
            'body1-bold': 'p',
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            textDecoration: 'none',
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '16px',
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            marginTop: spacing(4),
            marginBottom: spacing(4),
          },
          vertical: {
            marginLeft: '32px',
            marginRight: '32px',
            marginTop: 0,
            marginBottom: 0,
            height: 'auto',
          },
          light: {
            backgroundColor: 'rgba(255,255,255,0.5)',
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            color: oldColors.secondary,
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          regular: {
            minHeight: '60px !important',
          },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: {
            backgroundColor: '#fff',
          },
        },
      },
    },
  },
  svSE
);

// Composing new theme based on previous
theme = createTheme(theme, {
  typography: {
    'body1-bold': {
      ...theme.typography.body1,
      fontWeight: 'bold',
    },
    'body2-bold': {
      ...theme.typography.body2,
      fontWeight: 'bold',
    },
  },
});

const themeWithResponsiveFonts = responsiveFontSizes(theme, { factor: 3 });

export default function AppThemeProvider({ children }: { children: ReactNode }) {
  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={themeWithResponsiveFonts}>{children}</ThemeProvider>
    </StyledEngineProvider>
  );
}
